import { useState } from "react";
import {
  cocoonInvestmentSchema, COCOON_PACKAGES, COCOON_TIMELINES, COCOON_EXPERIENCE,
  cocoonPackageLabels, cocoonTimelineLabels, cocoonExperienceLabels,
  type CocoonInvestmentPayload,
} from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { RadioGroup } from "../components/RadioGroup";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, Stat, SectionHeader } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function CocoonInvestment() {
  const [form, setForm] = useState<Partial<CocoonInvestmentPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return (
    <SuccessScreen message="Спасибо! Свяжусь лично и предложу удобный формат — Zoom или личную встречу на Бали. На встрече покажу проект и инвестмодель." />
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = cocoonInvestmentSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("COCOON_INVESTMENT", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="Cocoon Resort"
        title="Стать акционером wellness-отеля"
        subtitle="20 юнитов-коконов, ресторан, SPA. Мундук, север Бали. Equity-инвестиции в реальный бизнес."
        icon="🌿"
        iconColor="brown"
      />
      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            Это не глемпинг и не очередной бутик-отель.
            Архитектурный life-experience hotel, построенный вокруг идеи личной трансформации.
          </Lead>
          <P>
            20 юнитов-коконов в Мундуке (север Бали, 1 200 м над уровнем моря).
            Butterfly Sanctuary, SPA как wellness-destination, ресторан как часть восстановления.
          </P>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Stat value="19,5%" label="средний ROI/год (5 лет)" />
          <Stat value="32%" label="breakeven загрузка" />
          <Stat value="18 мес" label="до старта дивидендов" />
          <Stat value="30 лет" label="горизонт инвестирования" />
        </div>

        <Card>
          <H3>Что уже сделано</H3>
          <CheckList items={[
            "Земля выбрана и проверена (топосъёмка, геология, юридическая чистота, 80 лет аренды)",
            "Команда собрана: основатель, арт-директор студии 19Buro, операционный директор с 15-летним опытом",
            "Финансовая модель прошла стресс-сценарии",
            "Программа амбассадоров — гарантирует загрузку через wellness-ретриты",
          ]} />
        </Card>

        <Card>
          <H3>3 пакета участия</H3>
          <div className="space-y-2 mt-2">
            <div className="ios-callout-muted">
              <b>🌱 Старт — $40 000</b> · 1% доли · ROI 16,5%/год · доход за 30 лет ≈ $374 575
            </div>
            <div className="ios-callout-muted">
              <b>🤝 Партнёр — $190 000</b> · 5% доли · ROI 17,4%/год · доход за 30 лет ≈ $1 872 877
            </div>
            <div className="ios-callout-muted">
              <b>💎 Со-инвестор — $350 000</b> · 10% доли · ROI 18,9%/год · доход за 30 лет ≈ $3 745 755
            </div>
            <P>Участие более 10% обсуждается индивидуально.</P>
          </div>
        </Card>

        <Card>
          <H3>Финансовая устойчивость</H3>
          <P>
            Breakeven всего на 32% загрузке (у конкурентов на Бали — 55%+).
            При среднегодовой загрузке у конкурентов 65–75% у проекта значительный запас прочности.
            Даже при 50% загрузке проект приносит ~$436 540 прибыли в год.
          </P>
        </Card>

        <div className="ios-callout">
          📞 Закрытое предложение. Презентацию покажу лично — на Zoom или при встрече на Бали.
        </div>
      </div>

      <SectionHeader>Оставить заявку</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт (Telegram / WhatsApp)" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
        </Field>
        <Field label="Рассматриваемый пакет" error={errors["package"]}>
          <RadioGroup value={form.package} onChange={(v) => setForm({ ...form, package: v as any })}
            options={COCOON_PACKAGES.map((v) => ({ value: v, label: cocoonPackageLabels[v] }))} />
        </Field>
        <Field label="Когда готов входить?" error={errors["timeline"]}>
          <RadioGroup value={form.timeline} onChange={(v) => setForm({ ...form, timeline: v as any })}
            options={COCOON_TIMELINES.map((v) => ({ value: v, label: cocoonTimelineLabels[v] }))} />
        </Field>
        <Field label="Опыт инвестирования" error={errors["experience"]}>
          <RadioGroup value={form.experience} onChange={(v) => setForm({ ...form, experience: v as any })}
            options={COCOON_EXPERIENCE.map((v) => ({ value: v, label: cocoonExperienceLabels[v] }))} />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Записаться на встречу</SubmitButton>
        </div>
      </form>
    </div>
  );
}
